import React, { useEffect, useMemo } from 'react'
import { CircularProgress, useTheme } from '@mui/material'
import Text from '../../Text'
import TextFieldCustom from '../../TextFieldCustom'
import TemplatePreviewBubble from '../../TemplatePreviewBubble'
import useModelosAprovados from './useModelosAprovados'
import { nomeDoTemplate } from '../../../libs/metaTemplates'
import { lerSnippetTemplate, variaveisDoTemplate, rotuloDaVariavel } from '../../../libs/scheduledMessages'

// Seletor de modelo aprovado da API Oficial + o que entra em cada campo dele.
// Usado pelo agendamento e pelo disparo em massa.
//
// Só aparece modelo APROVADO (em qualquer idioma: o envio sai no idioma em que
// o modelo está instalado). Com `soCorpoVariavel`, some também o modelo que pede
// parâmetro fora do corpo (cabeçalho de mídia ou com variável, botão de link
// variável) — o disparo em massa só preenche as variáveis do corpo.
//
// `valores` é indexado pela ordem dos campos (variaveisDoTemplate), como o
// agendamento sempre guardou. Quem precisa de "valor de {{n}}" converte.
// `previa`: 'texto' (corpo cru, como o agendamento) ou 'balao' (balão do WhatsApp).

// Por que o modelo não serve a um envio que só preenche o corpo — ou null.
function motivoForaDoCorpo(template) {
  for (const c of template?.components || []) {
    const tipo = String(c?.type || '').toUpperCase()
    if (tipo === 'HEADER') {
      const formato = String(c?.format || 'TEXT').toUpperCase()
      if (formato !== 'TEXT') return 'Pede imagem, vídeo ou documento no cabeçalho, que o disparo não envia.'
      if (/\{\{\s*\d+\s*\}\}/.test(String(c?.text || ''))) return 'Pede um campo no cabeçalho, que o disparo não preenche.'
    }
    if (tipo === 'BUTTONS') {
      const variavel = (c?.buttons || []).some((b) => {
        const t = String(b?.type || '').toUpperCase()
        return !['QUICK_REPLY', 'PHONE_NUMBER', 'URL'].includes(t) || (t === 'URL' && /\{\{.*?\}\}/.test(String(b?.url || '')))
      })
      if (variavel) return 'Pede link variável ou código no botão, que o disparo não preenche.'
    }
  }
  return null
}

function pedeParametroForaDoCorpo(template) {
  return !!motivoForaDoCorpo(template)
}

export default function SeletorDeModelo({
  api,
  secret,
  nome,
  valores = [],
  variaveis = [],
  snippetSalvo = '',
  onChange,
  onErro,
  soCorpoVariavel = false,
  previa = 'texto',
  textoSemModelos
}) {
  const theme = useTheme()
  const servem = (lista) => lista.filter((t) => !soCorpoVariavel || !pedeParametroForaDoCorpo(t))
  const { modelos, carregando, erro } = useModelosAprovados(api, secret, {
    // Editando: o que está salvo é o snippet; volta o modelo e os valores.
    onCarregou: (aprovados) => {
      const salvo = lerSnippetTemplate(snippetSalvo)
      if (!salvo) return
      const template = servem(aprovados).find((t) => nomeDoTemplate(t) === salvo.nome) || null
      onChange({ nome: salvo.nome, valores: salvo.valores, template })
    }
  })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const templates = useMemo(() => servem(modelos), [modelos, soCorpoVariavel])

  useEffect(() => {
    if (erro && onErro) onErro(erro)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erro])

  const templateAtual = useMemo(
    () => templates.find((t) => nomeDoTemplate(t) === nome) || null,
    [templates, nome]
  )
  const campos = useMemo(() => (templateAtual ? variaveisDoTemplate(templateAtual) : []), [templateAtual])

  const escolher = (novoNome) => {
    const template = templates.find((t) => nomeDoTemplate(t) === novoNome) || null
    onChange({ nome: novoNome, valores: [], template })
  }

  const preencher = (i, valor) => {
    const novo = [...valores]
    novo[i] = valor
    onChange({ nome, valores: novo, template: templateAtual })
  }

  if (carregando) {
    return (
      <div className="flex items-center gap-2 py-2">
        <CircularProgress size={18} />
        <Text component="span" className="text-sm">Carregando modelos aprovados…</Text>
      </div>
    )
  }

  if (!templates.length) {
    return (
      <Text className="font-light text-sm">
        {textoSemModelos || (
          <>Nenhum modelo aprovado em português nesta conexão. Crie e aprove um modelo em <b> Modelos Meta</b> antes de agendar.</>
        )}
      </Text>
    )
  }

  // Prévia em balão: TemplatePreviewBubble espera os valores pelo número da
  // variável ({{n}} → posição n − 1).
  const valoresPorNumero = []
  campos.forEach((n, i) => { valoresPorNumero[n - 1] = valores[i] })

  return (
    <>
      <TextFieldCustom
        label="Modelo aprovado"
        select
        SelectProps={{ native: true }}
        value={nome}
        onChange={(e) => escolher(e.target.value)}
      >
        <option value="">Escolha um modelo…</option>
        {templates.map((t) => {
          const n = nomeDoTemplate(t)
          return (
            <option key={n} value={n}>
              {n}{t.category ? ` · ${String(t.category).toLowerCase()}` : ''}
            </option>
          )
        })}
      </TextFieldCustom>

      {templateAtual && (
        <div className="mt-3">
          {previa === 'balao' ? (
            <div className="mb-3">
              <TemplatePreviewBubble template={templateAtual} values={valoresPorNumero} />
            </div>
          ) : (
            <div className="p-3 rounded-lg mb-3" style={{ border: `1px solid ${theme.palette.divider}` }}>
              <Text className="text-xs opacity-70 mb-1">Texto do modelo</Text>
              <Text className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>
                {(templateAtual.components || []).find((c) => String(c?.type).toUpperCase() === 'BODY')?.text || ''}
              </Text>
            </div>
          )}

          {campos.length === 0 ? (
            <Text className="font-light text-sm">Este modelo não tem campos para preencher.</Text>
          ) : (
            <>
              <Text className="text-xs opacity-70 mb-1">O que entra em cada campo do modelo</Text>
              {campos.map((n, i) => (
                <div key={n} className="mb-2">
                  <TextFieldCustom
                    label={`Campo ${n} — ${rotuloDaVariavel(templateAtual, n)}`}
                    value={valores[i] || ''}
                    onChange={(e) => preencher(i, e.target.value)}
                    placeholder="texto fixo ou {{nome}}"
                  />
                </div>
              ))}
              <div className="flex items-center flex-wrap mt-1">
                <Text component="span" className="text-xs mr-2 opacity-70">Variáveis disponíveis:</Text>
                {variaveis.map((v) => (
                  <span
                    key={v}
                    className="text-xs px-2 py-1 rounded mr-2 mb-1 font-mono"
                    style={{
                      border: `1px dashed ${theme.palette.divider}`,
                      color: theme.palette.primary.main,
                      backgroundColor: `${theme.palette.primary.main}14`
                    }}
                  >
                    {`{{${v}}}`}
                  </span>
                ))}
              </div>
              <Text className="font-light text-xs mt-2">
                Cada destinatário recebe o modelo preenchido com os dados dele.
              </Text>
            </>
          )}
        </div>
      )}
    </>
  )
}

export { pedeParametroForaDoCorpo, motivoForaDoCorpo }
